import type { Fraction } from '@mimir/common/util/fractionType';
import { calculateRawAudioFrameBufferSize, calculateRawVideoFrameBufferSize, calculateSampleCountForFrameNumber, calculateSampleOffsetForFrameNumber } from '@mimir/common/transcoding/rawFrame';
import type { FrameProviderV2, Listener, FrameV2, FrameInfoV2, VideoInfoV2, AudioInfoV2, VideoFrameV2, AudioFrameV2, PreloadInfo } from './frameProvider';
import type { Resolution } from '@mimir/common/transcoding/types';
import { BLACK, createPaintContext, PaintBuffer, YELLOW } from '@mimir/common/pixelManipulation/paintContext';
import { drawText } from '@mimir/common/pixelManipulation/drawText';
import { divideFractions, formatFractionAsExpression } from '@mimir/common/util/integerMath';
import { BufferCache } from './refCountedBuffer';

export function createDummyFrameProvider(resolution: Resolution, frameRate: Fraction, sampleRate: number, bufferCache: BufferCache): FrameProviderV2 {
  return createDummyFrameProviderInternal(resolution, frameRate, sampleRate, bufferCache);
}

// ------

function createDummyFrameProviderInternal(resolution: Resolution, frameRate: Fraction, sampleRate: number, bufferCache: BufferCache): FrameProviderV2 {
  const audioInfo : AudioInfoV2 = {
    sampleRate: 48000,
    channelCount: 2,
    format: 'float32',
  };
  const videoInfo : VideoInfoV2 = {
    resolution,
    pixelFormat: 'rgba',
    fieldOrder: 'progressive',
  };

  const rawInfo : FrameInfoV2 = {
    frameRate,
    video: videoInfo,
    audio: audioInfo,
  };

  const squareSize = 30.0;
  const halfSquareSize = squareSize / 2.0;
  const bounceSpace = { width: (resolution.width / 2) - squareSize, height: (resolution.height / 2) - squareSize };

  let closed = false;

  return {
    setListener(listener: Listener): void {
      // no-op
    },

    async getInfo(_signal: AbortSignal): Promise<FrameInfoV2> {
      return rawInfo;
    },
    async getFrame(frameNumber: number, _preloadHint: PreloadInfo | null, _signal: AbortSignal): Promise<FrameV2> {
      if (closed) {
        throw new Error('getFrame on a closed FrameProvider.');
      }
      let videoBufferSize = calculateRawVideoFrameBufferSize(resolution.width, resolution.height, videoInfo.pixelFormat);

      let squareRepeatX = frameNumber % (bounceSpace.width * 2)
      let squareRepeatY = frameNumber % (bounceSpace.height * 2)
      let squareX = ((squareRepeatX >= bounceSpace.width) ? (bounceSpace.width * 2) - squareRepeatX : squareRepeatX) + halfSquareSize;
      let squareY = ((squareRepeatY >= bounceSpace.height) ? (bounceSpace.height * 2) - squareRepeatY : squareRepeatY) + halfSquareSize;

      const beep =
        (squareRepeatX === 0) ||
        (squareRepeatY === 0) ||
        (squareRepeatX === bounceSpace.width) ||
        (squareRepeatY === bounceSpace.height);

      const videoBuffer = bufferCache.allocateBuffer(
        videoBufferSize,
        (videoBuffer) => {
          let paintBuffer : PaintBuffer = {
            width: resolution.width,
            height: resolution.height,
            pixelFormat: videoInfo.pixelFormat,
            pixelData: videoBuffer,
          };

          let paintContext = createPaintContext(paintBuffer);

          for (let i = -halfSquareSize; i < halfSquareSize; i++) {
            for (let j = -halfSquareSize; j < halfSquareSize; j++) {
              paintContext.setPixelRgb(squareX + i, squareY + j, { r: 255, g: 0, b: 255 });
            }
          }

          const exactPos = divideFractions({ numerator: frameNumber, denominator: 1 }, frameRate);

          drawText(paintContext, 0, 0, YELLOW, BLACK, `pos=${formatFractionAsExpression(exactPos)}`);
        });

      const videoFrame : VideoFrameV2 = { ...videoInfo, buffer: videoBuffer };

      const sampleOffset = calculateSampleOffsetForFrameNumber(sampleRate, frameRate, frameNumber);
      const sampleCount = calculateSampleCountForFrameNumber(sampleRate, frameRate, frameNumber);
      const audioBufferSize = calculateRawAudioFrameBufferSize(audioInfo, sampleCount);

      const audioBuffer =
        bufferCache.allocateBuffer(
          audioBufferSize,
          (buffer: Uint8Array) => {
            const samples = new Float32Array(buffer.buffer);

            const notes = [0, 2, 4, 5, 7, 9, 11];
            const beepFrequency = 440 * Math.pow(Math.pow(2, (1 / 12.0)), notes[frameNumber % 7]);
            for (let i = 0; i < sampleCount; i++) {
              const time = (sampleOffset + i) / audioInfo.sampleRate;
              const sample = Math.sin(time * 220.0 * Math.PI * 2.0) * 0.1;
              const beepSample = Math.sin(time * beepFrequency * Math.PI * 2.0) * 0.2;
              const beepVolume = beep ? (1.0 - Math.cos((i / sampleCount) * Math.PI * 2.0)) / 2.0 : 0.0;
              for (let j = 0; j < audioInfo.channelCount; j++) {
                samples[(i * audioInfo.channelCount) + j] = sample + (beepSample * beepVolume);
              }
            }

          }
        );

      let audioFrame : AudioFrameV2 = {
        ...audioInfo,
        sampleCount,
        sampleOffset,
        buffer: audioBuffer,
      }

      return {
        frameRate,
        frameNumber,
        video: videoFrame,
        audio: audioFrame,
      };
    },
    async preload(
      _frameStart: number,
      _preloadInfo: PreloadInfo,
      _signal: AbortSignal
    ): Promise<void> {
      // TODO: Maybe it would be interesting to simulate some form of buffering cost.
    },
    close(): void {
      closed = true;
    }
  }
}
